/**
 * Ajaxinate
 * Copyright (c) 2017 Elkfox Co Pty Ltd (elkfox.com)
 * Licensed under the MIT license.
 */

const Ajaxinate = function ajaxinateConstructor(config) {
  const settings = config || {};
  
  const defaults = {
    pagination: '.AjaxinatePagination',
    container: '.AjaxinateLoop',
    offset: 0,
    loadingText: 'Loading',
    callback: null
  };
  
  // Merge custom configs with defaults
  this.settings = Object.assign(defaults, settings);
  
  // Initial selectors
  this.addScrollListeners = this.addScrollListeners.bind(this);
  this.addClickListener = this.addClickListener.bind(this);
  this.checkIfPaginationInView = this.checkIfPaginationInView.bind(this);
  this.preventMultipleClicks = this.preventMultipleClicks.bind(this);
  this.removeClickListener = this.removeClickListener.bind(this);
  this.removeScrollListener = this.removeScrollListener.bind(this);
  this.requestContent = this.requestContent.bind(this);
  this.endlessScroll = this.endlessScroll.bind(this);
  this.loadMore = this.loadMore.bind(this);
  
  // Grab the pagination element
  this.paginationElement = document.querySelector(this.settings.pagination);
  this.containerElement = document.querySelector(this.settings.container);
  
  // Initialization check
  this.initialize();
};

Ajaxinate.prototype.initialize = function initializeTheCorrectFunctionsBasedOnTheMethod() {
  if (!this.containerElement) { return; }
  
  if (this.paginationElement) {
    const method = this.paginationElement.getAttribute('data-ajaxinate-method');
    
    if (method === 'click') {
      this.addClickListener();
    } else {
      this.addScrollListeners();
    }
  } else {
    // No pagination element found, so exit
    return;
  }
};

Ajaxinate.prototype.addScrollListeners = function addScrollListenersToDocument() {
  if (this.paginationElement) {
    document.addEventListener('scroll', this.checkIfPaginationInView);
    window.addEventListener('resize', this.checkIfPaginationInView);
    window.addEventListener('orientationchange', this.checkIfPaginationInView);
    this.checkIfPaginationInView();
  }
};

Ajaxinate.prototype.addClickListener = function addClickListenerToButton() {
  if (this.paginationElement) {
    this.nextPageLinkElement = this.paginationElement.querySelector('a');
    this.clickActive = true;
    
    if (this.nextPageLinkElement) {
      this.nextPageLinkElement.addEventListener('click', this.loadMore);
    }
  }
};

Ajaxinate.prototype.checkIfPaginationInView = function checkIfPaginationElementIsScrolledIntoTheViewport() {
  if (!this.paginationElement) { return; }
  
  let rect = this.paginationElement.getBoundingClientRect();
  let buffer = this.settings.offset || 0;
  
  // Use a buffer to trigger the loading earlier
  const windowHeight = window.innerHeight + buffer;
  const top = rect.top;
  
  if (top <= windowHeight && top >= 0) {
    this.nextPageLinkElement = this.paginationElement.querySelector('a');
    this.removeScrollListener();
    
    if (this.nextPageLinkElement) {
      this.nextPageLinkElement.innerHTML = this.settings.loadingText;
      this.nextPageUrl = this.nextPageLinkElement.href;
      this.clickActive = true;
      
      // Load the next page content
      this.loadMore();
    }
  }
};

Ajaxinate.prototype.loadMore = function loadTheNextPage(event) {
  if (event) {
    event.preventDefault();
  }
  
  // Guard against multiple clicks
  if (this.clickActive) {
    this.nextPageLinkElement.innerHTML = this.settings.loadingText;
    this.nextPageUrl = this.nextPageLinkElement.href;
    this.clickActive = false;
    
    // Request the content
    this.requestContent();
  }
};

Ajaxinate.prototype.requestContent = function requestContentAjaxCall() {
  if (!this.nextPageUrl) { return; }
  
  // Show a loading animation or message
  this.setLoading(true);
  
  // Fetch the content
  fetch(this.nextPageUrl)
    .then(response => {
      // Check if the response is valid
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(html => {
      // Parse the new content
      const parser = new DOMParser();
      const htmlDoc = parser.parseFromString(html, 'text/html');
      const newContainer = htmlDoc.querySelector(this.settings.container);
      const newPagination = htmlDoc.querySelector(this.settings.pagination);
      
      // Append the new items to the container
      if (newContainer) {
        this.containerElement.insertAdjacentHTML('beforeend', newContainer.innerHTML);
      }
      
      // Handle smooth scroll animation (optional)
      this.smoothScrollToNewContent();
      
      // Replace the pagination element
      if (newPagination) {
        this.paginationElement.innerHTML = newPagination.innerHTML;
        this.nextPageLinkElement = this.paginationElement.querySelector('a');
        
        // If there's another page, set up for the next request
        if (this.nextPageLinkElement) {
          this.nextPageUrl = this.nextPageLinkElement.href;
          this.clickActive = true;
        } else {
          // No more pages, remove the pagination element
          this.removePaginationElement();
        }
      } else {
        // No more pages, remove the pagination element
        this.removePaginationElement();
      }
      
      // If endless scroll, set up the next scroll check
      if (this.settings.callback && typeof this.settings.callback === 'function') {
        this.settings.callback(this.containerElement);
      }
      
      // Re-initialize scroll listeners for endless scroll
      this.initialize();
      
      // Remove loading state
      this.setLoading(false);
    })
    .catch(error => {
      console.error('Error fetching content:', error);
      this.setLoading(false);
      this.clickActive = true;
    });
};

Ajaxinate.prototype.smoothScrollToNewContent = function smoothScrollToNewContent() {
  // Add smooth scrolling behavior here if needed
  // This is handled by the existing smooth scroll implementation
};

Ajaxinate.prototype.setLoading = function setLoadingState(isLoading) {
  if (isLoading) {
    // Set loading state
    if (this.nextPageLinkElement) {
      this.nextPageLinkElement.innerHTML = this.settings.loadingText;
      this.nextPageLinkElement.classList.add('is-loading');
    }
  } else {
    // Remove loading state
    if (this.nextPageLinkElement) {
      this.nextPageLinkElement.innerHTML = 'Load more';
      this.nextPageLinkElement.classList.remove('is-loading');
    }
  }
};

Ajaxinate.prototype.removePaginationElement = function removeThePaginationElement() {
  this.paginationElement.parentNode.removeChild(this.paginationElement);
  this.paginationElement = null;
  this.nextPageLinkElement = null;
  this.clickActive = false;
};

Ajaxinate.prototype.removeClickListener = function removeClickEventListener() {
  if (this.nextPageLinkElement) {
    this.nextPageLinkElement.removeEventListener('click', this.loadMore);
  }
};

Ajaxinate.prototype.removeScrollListener = function removeScrollEventListener() {
  document.removeEventListener('scroll', this.checkIfPaginationInView);
  window.removeEventListener('resize', this.checkIfPaginationInView);
  window.removeEventListener('orientationchange', this.checkIfPaginationInView);
};

Ajaxinate.prototype.endlessScroll = function handleEndlessScroll() {
  if (this.settings.method === 'scroll') {
    this.addScrollListeners();
  }
};

Ajaxinate.prototype.preventMultipleClicks = function handlePotentialMultipleClicks() {
  this.nextPageLinkElement.removeEventListener('click', this.loadMore);
};

// Export as a global or ES module
if (typeof module === 'object' && module.exports) {
  module.exports = Ajaxinate;
} else {
  window.Ajaxinate = Ajaxinate;
} 